import { TFunction } from 'i18next';
import React from 'react';

import { Separator } from '../../../../components/layout/Separator';
import { RecordBooleanType } from '../../../../types';
import { QueuesWithColors } from '../../types';

export interface MultiQueueChartLegendProps {
  dataPerQueueVisibility: RecordBooleanType;
  t: TFunction;
  queueIds: string[];
  queues: QueuesWithColors;
  toggleQueueVisibility: (queueId: string) => void;
  updateVisibilityForAllQueues: () => void;
}

export const ChartLegendItem: React.FC<{
  color: string;
  children: React.ReactNode;
  dataTestId: string;
  isVisible: boolean;
  toggleVisible: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ color, dataTestId, isVisible, children, toggleVisible }) => (
  <button
    aria-checked={isVisible}
    className="scheduling-chart__legend__item"
    data-test-id={dataTestId}
    onClick={toggleVisible}
    role="checkbox"
    type="button"
  >
    <span className="scheduling-chart__legend__item-square">
      <span style={{ backgroundColor: color }} />
    </span>
    {children}
  </button>
);

export const MultiQueueChartLegend: React.FC<MultiQueueChartLegendProps> = ({
  dataPerQueueVisibility,
  queueIds,
  queues,
  t,
  toggleQueueVisibility,
  updateVisibilityForAllQueues,
}) => {
  const containHiddenQueue = Object.values(dataPerQueueVisibility).find((data) => !data) !== undefined;

  const handleClick = (e: React.MouseEvent) => {
    // blur only when invoked by mouse click
    if (e.nativeEvent.detail && e.currentTarget.contains(document.activeElement)) {
      (document.activeElement as HTMLElement).blur();
    }
  };

  return (
    <div className="scheduling-chart__legend--multiple-queues">
      {containHiddenQueue && (
        <>
          <button
            className="scheduling-chart__legend__button"
            data-test-id="scheduling-chart:legend:show-me-all-button"
            onClick={(e) => {
              handleClick(e);
              updateVisibilityForAllQueues();
            }}
            type="button"
          >
            {t('layout.showMeAll')}
          </button>
          <Separator showBorder />
        </>
      )}
      <div className="scheduling-chart__legend">
        {queueIds.map((queueId) => (
          <ChartLegendItem
            color={queues[queueId].color}
            dataTestId={`scheduling-chart:legend:item(${queueId})`}
            isVisible={dataPerQueueVisibility[queueId]}
            key={queueId}
            toggleVisible={(e) => {
              handleClick(e);
              toggleQueueVisibility(queueId);
            }}
          >
            {queues[queueId].name}
          </ChartLegendItem>
        ))}
      </div>
    </div>
  );
};
